import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ServiceFlipCard from './ServiceFlipCard';

const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

function Services({ services }) {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleToggle = (id) => {
    setExpandedCardId((current) => (current === id ? null : id));
  };

  return (
    <section id="services" className="relative section-space bg-snow/60 overflow-hidden" data-aos="fade-up">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-accent/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-accent/10 blur-3xl"
      />

      <div className="section-wrap relative">
        <div className="mb-14 grid grid-cols-1 gap-8 md:mb-16 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-6">
            <p className="meta-label">01 / Capabilities</p>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.86] tracking-[-0.03em] md:text-7xl">
              Signature
              <br />
              <span className="italic text-transparent [-webkit-text-stroke:1.2px_rgba(16,16,16,0.45)]">Services</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-6">
            <p className="copy-refined text-sm text-ink/72 md:text-base">
              Premium freelance web development services — explained in plain language, priced for real businesses,
              and delivered with the care of a product team, not a contractor.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {['Full Stack', 'Design', 'AI', 'SEO', 'E-Commerce', 'Maintenance'].map((tag) => (
                <span
                  key={tag}
                  className="ui-chip rounded-full border border-ink/15 bg-snow/65 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/70"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          variants={listVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 gap-7 md:grid-cols-2 xl:grid-cols-3 xl:gap-8"
        >
          {services.map((service, index) => (
            <motion.div key={service.id} variants={itemVariants} className="relative">
              <span className="absolute -top-3 left-5 z-20 inline-flex h-7 items-center justify-center rounded-full border border-ink/15 bg-snow px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/70">
                {`0${index + 1}`}
              </span>
              <ServiceFlipCard
                title={service.title}
                customerView={service.customerView}
                description={service.description}
                points={service.deliverables}
                tags={service.tags}
                tools={service.tools}
                imageUrl={service.imageUrl}
                mobileExpanded={expandedCardId === service.id}
                onToggle={() => handleToggle(service.id)}
              />
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-12 flex flex-col items-stretch gap-5 rounded-3xl border border-ink/12 bg-snow/70 p-5 text-center md:mt-20 md:flex-row md:items-center md:justify-between md:p-7 md:text-left">
          <div>
            <p className="meta-label">Not sure which service fits?</p>
            <p className="mt-2 font-display text-lg font-bold uppercase leading-tight tracking-[-0.01em] md:text-2xl">
              Let&apos;s scope your project in a 20-minute call.
            </p>
          </div>
          <a
            href="#contact"
            className="ui-button-dark inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-ink bg-coal px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
          >
            Book a free call
            <span className="ui-arrow-hover text-accent">↗</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default Services;
